
/**
 * @file LoadingIndicator.jsx
 * @description Enhanced loading indicators with different states for routing vs response generation
 *
 * @component LoadingIndicator
 * @props None
 *
 * @imports
 * - React: The core library for building the component.
 * - styles (from ChatApp.module.css): For styling the loading dots and their container.
 *
 * @purpose This component's sole function is to provide visual feedback to the user that the application is busy processing a request (e.g., waiting for the Route AI decision or for the initial response from a model). It consists of three pulsing dots, a common and easily understood loading pattern.
 */

import React from 'react';
import styles from '../../styles/ChatApp.module.css';

/**
 * A visual indicator with two different modes:
 * 1. 'routing' - Shows 3 dots with "Finding best model..." text
 * 2. 'generating' - Shows skeleton text loader indicating incoming content
 * 
 * @param {string} type - Either 'routing' or 'generating'
 */
const LoadingIndicator = ({ type = 'generating' }) => {
    if (type === 'routing') {
        return (
            <div className={styles.loadingWrapper}>
                <div className={styles.loadingBubble}>
                    {/* Three pulsing dots */}
                    <div className={`${styles.loadingDot} ${styles.pulse1}`}></div>
                    <div className={`${styles.loadingDot} ${styles.pulse2}`}></div>
                    <div className={`${styles.loadingDot} ${styles.pulse3}`}></div>
                </div>
                <span className={styles.loadingText}>Finding the best model...</span>
            </div>
        );
    }

    // Default 'generating' type - skeleton text loader
    return (
        <div className={styles.skeletonWrapper}>
            <div className={styles.skeletonBubble}>
                <div className={styles.skeletonLine} style={{ width: '80%' }}></div>
                <div className={styles.skeletonLine} style={{ width: '95%' }}></div>
                <div className={styles.skeletonLine} style={{ width: '70%' }}></div>
            </div>
        </div>
    );
};

export default LoadingIndicator;